import { TransactionLineItem } from 'webReservations/checkout/apiHelpers';
import { useStripePaymentElements } from 'webReservations/checkout/useStripePaymentElements';
import { waitForConfirmedReservation } from 'webReservations/checkout/waitForConfirmedReservation';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { useGuestLandingPaths } from 'webReservations/hooks/useGuestLandingPaths';
import { Button, ButtonVariants } from '@components/button/Button';
import { Error as ErrorMessage } from '@components/error/Error';
import { PaymentElement } from '@components/stripe/PaymentElement';
import { errorToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import { useMutation } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import typography from '~styles/typography.scss';
import { bookEventReservation } from './apiHelpers';
import {
  RESTAURANT_EVENT_TIER_ID,
  SELECTED_ADD_ONS,
  SELECTED_FLOOR_PLAN_TABLE_ID,
} from './constants';
import type { SelectedAddOn } from './apiHelpers';
import styles from './EventCheckoutForm.scss';

interface EventCheckoutFormProps {
  guestCount: number;
  disabled: boolean;
  transactionLineItems: TransactionLineItem[];
  tax: number;
  fee: number;
  total: number;
  subtotal: number;
}

const useBookEventReservation = () => {
  const { restaurantDetails } = useRestaurantContext();
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const restaurantEventTierId = searchParams.get(RESTAURANT_EVENT_TIER_ID);
  const selectedFloorPlanTableId = searchParams.get(
    SELECTED_FLOOR_PLAN_TABLE_ID,
  );
  const selectedAddOnsParam = searchParams.get(SELECTED_ADD_ONS);
  const selectedAddOns: SelectedAddOn[] | undefined = selectedAddOnsParam
    ? JSON.parse(selectedAddOnsParam)
    : undefined;

  return useMutation({
    mutationFn: ({
      guestCount,
      transactionLineItems,
      tax,
      fee,
      total,
    }: {
      guestCount: number;
      transactionLineItems: TransactionLineItem[];
      tax: number;
      fee: number;
      total: number;
    }) => {
      if (!eventId) throw new Error('Missing event id');
      if (!restaurantEventTierId) throw new Error('Missing event tier id');

      return bookEventReservation({
        restaurantId: restaurantDetails.id,
        eventId,
        guestCount,
        restaurantEventTierId,
        transactionLineItems,
        tax,
        fee,
        total,
        selectedFloorPlanTableId,
        selectedAddOns,
      });
    },
    onError: (error) => {
      errorToast({ message: error.message });
      reportAppError(error);
    },
  });
};

export const EventCheckoutForm = ({
  disabled,
  guestCount,
  transactionLineItems,
  tax,
  fee,
  total,
}: EventCheckoutFormProps) => {
  const navigate = useNavigate();
  const { restaurantDetails } = useRestaurantContext();
  const { reservationDetailsPath } = useGuestLandingPaths();
  const bookEventReservationMutation = useBookEventReservation();
  const { handlePaymentElementOnChange, processPaymentIntent, isFormComplete } =
    useStripePaymentElements();
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmationError, setConfirmationError] = useState<string | null>(
    null,
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setConfirmationError(null);
    const bookingResponse = await bookEventReservationMutation.mutateAsync({
      guestCount,
      transactionLineItems,
      tax,
      fee,
      total,
    });

    const { paymentIntentClientSecret, reservation } = bookingResponse;

    if (paymentIntentClientSecret) {
      await processPaymentIntent(paymentIntentClientSecret);
    }

    // The reservation is only confirmed once the payment webhook lands, so
    // wait for it here (mirroring `useBookReservation`'s table reservation
    // flow) rather than sending the guest to a confirmation page for a
    // reservation that's still pending.
    setIsConfirming(true);
    try {
      await waitForConfirmedReservation(reservation.id);
    } catch (error) {
      setConfirmationError(
        error instanceof Error
          ? error.message
          : 'Something went wrong confirming your reservation.',
      );
      return;
    } finally {
      setIsConfirming(false);
    }

    navigate(reservationDetailsPath(reservation.id, restaurantDetails), {
      replace: true,
    });
  };

  return (
    <form
      id="payment-form"
      className={styles.container}
      onSubmit={handleSubmit}
    >
      <h3 className={typography.h6}>Payment Method</h3>
      <PaymentElement onChange={handlePaymentElementOnChange} />
      <ErrorMessage message={confirmationError ?? undefined} />
      <div className={styles.buttons}>
        <Button
          label="Purchase with Credit Card"
          type="submit"
          variant={ButtonVariants.Primary}
          isDisabled={disabled || !isFormComplete || isConfirming}
          isLoading={isConfirming}
        />
        <Button
          label="Cancel"
          onClick={() => navigate(-1)}
          variant={ButtonVariants.Tertiary}
        />
      </div>
    </form>
  );
};
